import React, { useEffect, useState, useRef } from "react";
import {
  User,
  Mail,
  Lock,
  School,
  Bookmark,
  Award,
  X,
  Save,
  Camera,
} from "lucide-react";
import {
  getProfile,
  updateProfileImage,
  changePassword,
} from "server/Authentication/Requests";
import { Profile } from "server/Authentication/OutputsTypes";
import { parseSchoolClass, getInitials } from "components/common/genTextField";
import { getSchoolClassLabel } from "components/common/utils";

// Type pour les valeurs initiales
type InitialValuesType = {
  nom: string;
  prenom: string;
  school: string;
  niveauEtudes: string;
  serie: string;
};

// Système de traduction des classes
const CLASS_TRANSLATIONS: { [key: string]: string } = {
  // Classes de Terminale
  terminale_A1: "Terminale A1",
  terminale_A2: "Terminale A2",
  terminale_B: "Terminale B",
  terminale_C: "Terminale C",
  terminale_D: "Terminale D",
  terminale_G1: "Terminale G1",
  terminale_G2: "Terminale G2",
  terminale_G3: "Terminale G3",
  terminale_A: "Terminale A",
  "Tle C": "Terminale C",
  "Tle D": "Terminale D",
  "Tle G1": "Terminale G1",
  "Tle G2": "Terminale G2",
  "Tle G3": "Terminale G3",
  "Tle A1": "Terminale A1",
  "Tle A2": "Terminale A2",
  "Tle B": "Terminale B",
  "Tle A": "Terminale A",
};

// Fonction pour traduire la classe
const translateClass = (classData: string): string => {
  // Nettoyer la donnée reçue
  const cleanClass = classData.trim();

  // Vérifier si la traduction existe
  if (CLASS_TRANSLATIONS[cleanClass]) {
    return CLASS_TRANSLATIONS[cleanClass];
  }

  // Si pas de traduction exacte, essayer de parser et traduire
  try {
    const parsed = parseSchoolClass(cleanClass);
    if (parsed) {
      const key = `${parsed.level}_${parsed.serie}`;
      if (CLASS_TRANSLATIONS[key]) {
        return CLASS_TRANSLATIONS[key];
      }
    }
  } catch (error) {
    console.warn("Erreur lors du parsing de la classe:", cleanClass, error);
  }

  // Fallback: retourner la donnée originale si aucune traduction n'est trouvée
  console.warn(`Aucune traduction trouvée pour la classe: "${cleanClass}"`);
  return cleanClass;
};

interface TextFieldProps {
  title: string;
  value: string;
  onChange: (value: string) => void;
  isEditable?: boolean;
  isSecretField?: boolean;
  icon: React.ReactNode;
}

const TextField: React.FC<TextFieldProps> = ({
  title,
  value,
  onChange,
  isEditable = true,
  isSecretField = false,
  icon,
}) => {
  return (
    <div className="mb-6 relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {title}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
          {icon}
        </div>
        <input
          type={isSecretField ? "password" : "text"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={!isEditable}
          className={`w-full py-2 pl-10 pr-3 border ${
            isEditable
              ? "border-gray-300 focus:ring-2 focus:ring-indigo-600 focus:border-indigo-600"
              : "bg-gray-100 border-gray-200"
          } rounded-lg shadow-sm transition-all duration-200 ${
            !isEditable ? "cursor-not-allowed" : ""
          }`}
        />
      </div>
    </div>
  );
};

const Settings = () => {
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [niveauEtudes, setNiveauEtudes] = useState("");
  const [school, setSchoolName] = useState("");
  const [nameInits, setNameInits] = useState("");
  const [initialValues, setInitialValues] = useState<InitialValuesType | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [changesMade, setChangesMade] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Surveiller les changements
  useEffect(() => {
    if (initialValues) {
      const hasChanges =
        nom !== initialValues.nom ||
        prenom !== initialValues.prenom ||
        oldPassword !== "" ||
        newPassword !== "";

      setChangesMade(hasChanges);
    }
  }, [nom, prenom, oldPassword, newPassword, initialValues]);

  // Charger le profil
  useEffect(() => {
    setLoading(true);
    getProfile().then((res) => {
      if (res.status !== 200) {
        const { message } = res.body as { message: string };
        console.error("Error:", message);
      } else {
        const data = res.body as Profile;

        setNom(data.lastname);
        setPrenom(data.firstname);
        setSchoolName(data.schoolName);
        const translatedClass = getSchoolClassLabel(data.schoolClass);
        setNiveauEtudes(translatedClass);

        // Stocker les valeurs initiales
        setInitialValues({
          nom: data.lastname,
          prenom: data.firstname,
          school: data.schoolName,
          niveauEtudes: translatedClass,
          serie: data.schoolClass.split("_")[1] || "",
        });
      }
      setLoading(false);
    });
  }, []);

  // Mettre à jour les initiales
  useEffect(() => {
    setNameInits(getInitials(prenom, nom));
  }, [prenom, nom]);

  // Annuler les modifications
  const cancelChanges = () => {
    if (initialValues) {
      setNom(initialValues.nom);
      setPrenom(initialValues.prenom);
      setOldPassword("");
      setNewPassword("");
    }
  };

  // Sauvegarder les modifications
  const saveChanges = async () => {
    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      if (oldPassword) {
        const response = await changePassword({
          oldPassword: oldPassword,
          newPassword: newPassword,
        });

        if (response.status === 200) {
          setSuccessMessage("Mot de passe modifié avec succès");
          setOldPassword("");
          setNewPassword("");
          setChangesMade(false);
        } else {
          setErrorMessage(
            response.body.message ||
              "Erreur lors du changement de mot de passe",
          );
        }
      }
    } catch (error) {
      setErrorMessage("Une erreur est survenue lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // Afficher un indicateur de chargement
      setLoading(true);

      const response = await updateProfileImage({ file: file });

      if (response.status === 200 && "profileImage" in response.body) {
        // Mettre à jour l'image de profil immédiatement
        setProfileImage(response.body.profileImage);
        setChangesMade(true);
      } else {
        console.error(
          "Erreur lors de la mise à jour de la photo de profil:",
          response.body.message,
        );
      }
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour de la photo de profil:",
        error,
      );
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300">
      {/* En-tête */}
      <div className="border-b border-gray-100 px-6 py-4">
        <h1 className="text-xl font-bold text-gray-800">
          Informations personnelles
        </h1>
      </div>

      <div className="p-6">
        {/* Messages d'erreur et de succès */}
        {errorMessage && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {errorMessage}
          </div>
        )}
        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {successMessage}
          </div>
        )}

        {/* Section Avatar */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Profil</h2>
          <div className="flex items-center space-x-4">
            <div className="relative">
              {loading ? (
                <div className="h-16 w-16 rounded-full flex items-center justify-center bg-gray-100">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600"></div>
                </div>
              ) : profileImage ? (
                <img
                  src={profileImage}
                  alt="Photo de profil"
                  className="h-16 w-16 rounded-full object-cover"
                />
              ) : (
                <div className="h-16 w-16 rounded-full flex items-center justify-center bg-indigo-100 text-indigo-800 text-xl font-medium">
                  {nameInits}
                </div>
              )}
              {/* <input
                type="file"
                ref={fileInputRef}
                onChange={handleProfileImageChange}
                accept="image/*"
                className="hidden"
              /> */}
              <button
                onClick={triggerFileInput}
                disabled={loading}
                className={`absolute bottom-0 right-0 p-1 rounded-full shadow-md transition-colors duration-200 ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-amber-400 hover:bg-amber-500"
                }`}
              >
                {/* <Camera size={16} className="text-indigo-800" /> */}
              </button>
            </div>
            {/* <p className="text-sm text-gray-500">
              {loading
                ? "Mise à jour de la photo de profil..."
                : "Photo de profil utilisée dans votre compte et vos activités"}
            </p> */}
          </div>
        </div>

        {/* Formulaire */}
        <div className="space-y-6">
          {/* Nom et prénom - sur la même ligne sur PC */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TextField
              title="Nom"
              value={nom}
              onChange={setNom}
              icon={<User size={16} />}
              isEditable={false}
            />
            <TextField
              title="Prénom(s)"
              value={prenom}
              onChange={setPrenom}
              icon={<User size={16} />}
              isEditable={false}
            />
          </div>

          {/* Autres champs - en grille sur desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TextField
              title="Classe"
              value={niveauEtudes}
              onChange={setNiveauEtudes}
              icon={<School size={16} />}
              isEditable={false}
            />
            <TextField
              title="Ecole"
              value={school}
              onChange={setSchoolName}
              icon={<Award size={16} />}
              isEditable={false}
            />
          </div>

          {/* Mots de passe - sur la même ligne sur PC */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TextField
              title="Mot de passe actuel"
              value={oldPassword}
              onChange={setOldPassword}
              isSecretField={true}
              icon={<Lock size={16} />}
            />
            <TextField
              title="Nouveau mot de passe"
              value={newPassword}
              onChange={setNewPassword}
              isSecretField={true}
              icon={<Lock size={16} />}
            />
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-4 mt-8">
          {changesMade && (
            <button
              onClick={cancelChanges}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
            >
              <X size={16} className="mr-2" />
              Annuler
            </button>
          )}
          <button
            onClick={saveChanges}
            className={`flex items-center px-4 py-2 rounded-lg ${
              changesMade
                ? "bg-indigo-600 text-white hover:bg-indigo-700"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            } transition-colors duration-200`}
          >
            <Save size={16} className="mr-2" />
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
